// import { ConversationSummary } from "@hooks/useChatAgent";
import { Box, CircularProgress, InputAdornment, Typography, useTheme } from "@mui/material";
import dayjs from "dayjs";
import {
  Archive,
  MessageSquare,
  Plus,
  Search,
  Settings,
  Trash2
} from "lucide-react";
import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { ButtonDigited } from "../../../../components/Button/Button.variants";
import {
  ConversationDate,
  ConversationItem,
  ConversationItemContent,
  ConversationList,
  ConversationPreview,
  ConversationTitle,
  DeleteIcon,
  EmptyState,
  HistoryDisabledContainer,
  HistoryDisabledIcon,
  HistoryDisabledSubtitle,
  HistoryDisabledTitle,
  LoadingRow,
  NewChatSection,
  SearchInput,
  SearchSection,
  SettingsButton,
  SidebarFooter,
  SidebarHeader,
  SidebarRoot,
  SidebarTitleRow
  
} from "../../AISupportChat.styles";
import { ConversationSummary } from "../../AISupportChat.types";

interface ChatAgentSidebarProps {
  isPrelogin: boolean;
  conversationsList: ConversationSummary[];
  isLoading: boolean;
  activeSessionId: string;
  onSelect: (sessionId: string) => void;
  onNewChat?: () => void;
  onDelete?: (sessionId: string) => void;
  onSettings?: () => void;
  disabled?: boolean;
  historyEnabled?: boolean;
}

// Formato data per le card di conversazione, allineato al mockup:
//   < 1 minuto fa  → "Adesso"
//   stesso giorno  → "Oggi, HH:mm"
//   ieri           → "Ieri, HH:mm"
//   stesso anno    → "DD MMM, HH:mm" (es. "25 Apr, 10:22")
//   altrimenti     → "DD/MM/YY, HH:mm"
const formatRelativeDate = (iso?: string) => {
  if (!iso) return "";
  const d = dayjs(iso);
  if (!d.isValid()) return "";
  const now = dayjs();
  if (now.diff(d, "minute") < 1) return "Adesso";
  if (d.isSame(now, "day")) return `Oggi, ${d.format("HH:mm")}`;
  if (d.isSame(now.subtract(1, "day"), "day"))
    return `Ieri, ${d.format("HH:mm")}`;
  if (d.isSame(now, "year")) return d.format("DD MMM, HH:mm");
  return d.format("DD/MM/YY, HH:mm");
};

const ChatAgentSidebar = ({
  isPrelogin,
  conversationsList,
  isLoading,
  activeSessionId,
  onSelect,
  onNewChat,
  onDelete,
  onSettings,
  disabled,
  historyEnabled = true
}: ChatAgentSidebarProps) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const [search, setSearch] = useState("");

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return conversationsList;
    return conversationsList.filter((c) => {
      const haystack = `${c.title ?? ""} ${c.preview ?? ""}`.toLowerCase();
      return haystack.includes(q);
    });
  }, [conversationsList, search]);

  const handleDelete = (
    e: React.MouseEvent<HTMLButtonElement>,
    sid: string
  ) => {
    e.stopPropagation();
    if (disabled) return;
    onDelete && onDelete(sid);
  };
  if(isPrelogin) return <></>;
  return (
    <SidebarRoot theme={ theme }>
      <SidebarHeader>
        <SidebarTitleRow>
          <Box
            sx={ {
              alignItems: "center",
              backgroundColor: theme.customColors.systemSecondary01,
              borderRadius: "4px",
              color: theme.customColors.textPrimaryCta,
              display: "flex",
              flexShrink: 0,
              fontSize: "14px",
              fontWeight: 700,
              height: "40px",
              justifyContent: "center",
              width: "40px"
            } }
          >
            AI
          </Box>
          <Typography
            variant="h4"
            component={ "h2" }
            fontWeight={ 700 }
            fontSize={ "1.125rem" }
          >
            { t("Olivia") }
          </Typography>
        </SidebarTitleRow>
        <NewChatSection>
          <ButtonDigited 
            typology="primary"
            disabled={ disabled }
            endIcon={ <Plus size={ "1.125rem" } /> }
            onClick={ onNewChat }
            text={ t("new_chat") }
            sx={ {
              width: "100% !important"
            } }
          />
        </NewChatSection>
        { /* <AnimatePresence initial={ false } mode="wait"> */ }
        { historyEnabled && (
        //   <motion.div
        //     key="search-section"
        //     initial={ { opacity: 0, height: 0 } }
        //     animate={ { opacity: 1, height: "auto" } }
        //     exit={ { opacity: 0, height: 0 } }
        //     transition={ { duration: 0.24, ease: [0.2, 0.8, 0.2, 1] } }
        //     style={ { overflow: "hidden" } }
        //   >
          <SearchSection>
            <SearchInput
              fullWidth
              size="small"
              placeholder={ t("search_agent_history_placeholder") || "" }
              value={ search }
              onChange={ (e) => setSearch(e.target.value) }
              InputProps={ {
                startAdornment: (
                  <InputAdornment position="start">
                    <Search size={ 16 } color={ theme.customColors.border }/>
                  </InputAdornment>
                )
              } }
            />
          </SearchSection>
        //   </motion.div>
        ) }
        { /* </AnimatePresence> */ }
      </SidebarHeader>

      <ConversationList withTopBorder={ historyEnabled }>
        { /* <AnimatePresence initial={ false } mode="wait"> */ }
        { !historyEnabled ? (
        // <motion.div
        //   key="history-disabled"
        //   initial={ { opacity: 0 } }
        //   animate={ { opacity: 1 } }
        //   exit={ { opacity: 0 } }
        //   transition={ { duration: 0.28 } }
        // >
          <HistoryDisabledContainer>
            <HistoryDisabledIcon>
              <Archive size={ 20 } />
            </HistoryDisabledIcon>
            <HistoryDisabledTitle>
              { t("historyDisabledTitle") }
            </HistoryDisabledTitle>
            <HistoryDisabledSubtitle>
              { t("historyDisabledSubtitle") }
            </HistoryDisabledSubtitle>
          </HistoryDisabledContainer>
        // </motion.div>
        ) : isLoading && conversationsList.length === 0 ? (
        // <motion.div
        //   key="loading"
        //   initial={ { opacity: 0 } }
        //   animate={ { opacity: 1 } }
        //   exit={ { opacity: 0 } }
        //   transition={ { duration: 0.18 } }
        // >
          <LoadingRow>
            <CircularProgress size={ 18 } />
          </LoadingRow>
        // </motion.div>
        ) : filtered.length === 0 ? (
        // <motion.div
        //   key="empty"
        //   initial={ { opacity: 0 } }
        //   animate={ { opacity: 1 } }
        //   exit={ { opacity: 0 } }
        //   transition={ { duration: 0.18 } }
        // >
          <EmptyState>{ t("ai_no_conversations") }</EmptyState>
        // </motion.div>
        ) : (
        // <motion.div
        //   key="conversation-list"
        //   initial={ { opacity: 0 } }
        //   animate={ { opacity: 1 } }
        //   exit={ { opacity: 0 } }
        //   transition={ { duration: 0.18 } }
        // >
        //   <AnimatePresence initial={ false }>
          <>
            { filtered.map((c, index) => {
              const isActive = c.session_id === activeSessionId;
              const title =
                    c.title?.trim() ||
                    (c.message_count && c.message_count > 0
                      ? t("conversation")
                      : t("newConversation"));
              const preview = c.preview?.trim() || "";
              const date = formatRelativeDate(c.updated_at ?? c.created_at);
              return (
              // <motion.div
              //   key={ c.session_id }
              //   layout
              //   initial={ { opacity: 0, scale: 0.96 } }
              //   animate={ { opacity: 1, scale: 1 } }
              //   exit={ { opacity: 0, scale: 0.96 } }
              //   transition={ {
              //     layout: { duration: 0.28, ease: [0.2, 0.8, 0.2, 1] },
              //     opacity: { duration: 0.16 },
              //     scale: { duration: 0.16 }
              //   } }
              // >
                <>
                  <ConversationItem
                    active={ isActive }
                    onClick={ () => onSelect(c.session_id) }
                    key={  index }
                  >
                    <MessageSquare
                      size={ "1.125rem" }
                      color={ theme.customColors.accentVariantA }
                    />
                    <ConversationItemContent>
                      <ConversationTitle>{ title }</ConversationTitle>
                      <ConversationPreview>{ preview }</ConversationPreview>
                      { date && <ConversationDate>{ date }</ConversationDate> }
                    </ConversationItemContent>
                    <DeleteIcon
                      size="small"
                      aria-label={ t("deleteConversation") || "Delete" }
                      onClick={ (e) => handleDelete(e, c.session_id) }
                      disabled={ disabled }
                    >
                      <Trash2 
                        size={ "1.125rem" }
                      />
                    </DeleteIcon>
                  </ConversationItem>
                </>
              // </motion.div>
              );
            }) }
          </>
        //   </AnimatePresence>
        // </motion.div>
        ) }
        { /* </AnimatePresence> */ }
      </ConversationList>

      { onSettings && (
        <SidebarFooter>
          <SettingsButton onClick={ onSettings } disabled={ disabled }>
            <Settings size={ 18 } />
            { t("settings") }
          </SettingsButton>
        </SidebarFooter>
      ) }
    </SidebarRoot>
  );
};

export default ChatAgentSidebar;
